import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useProjectContext } from '@/context/ProjectContext';
import ProjectScopeBanner from '@/components/common/ProjectScopeBanner';
import { useGetAllProjectTasksQuery, useGetTasksQuery, useGetProjectsQuery, useGetSprintsQuery } from '@/store/api/apiSlice';
import ProjectCreationModal from '@/components/projects/ProjectCreationModal';
import TaskDetailsModal from '@/components/tasks/TaskDetailsModal';
import { TaskStatus, TaskPriority } from '@/types';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { getUserDisplayName } from '@/utils/user';
import { LoadingOverlay } from '@/components/common/LoadingOverlay';
import { useFeature } from '@/hooks/useFeatureAccess';
import { differenceInCalendarDays, format, startOfQuarter, startOfYear, subDays } from 'date-fns';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, Filler);

const PROJECT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#EF4444', '#14B8A6'];

const getLast7DayLabels = () => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return { label: days[date.getDay()], key: date.toISOString().slice(0, 10) };
  });
};

const getLast4WeekLabels = () => {
  return Array.from({ length: 4 }, (_, i) => {
    const end = new Date();
    end.setDate(end.getDate() - i * 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return {
      label: `W${4 - i} (${format(start, 'MMM d')})`,
      startKey: start.toISOString().slice(0, 10),
      endKey: end.toISOString().slice(0, 10),
    };
  }).reverse();
};

function getTimeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('dashboard.timeAgoJustNow');
  if (minutes < 60) return t('dashboard.timeAgoMinutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('dashboard.timeAgoHours', { count: hours });
  const days = Math.floor(hours / 24);
  return t('dashboard.timeAgoDays', { count: days });
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, isProjectManager } = useAuth();
  const navigate = useNavigate();
  const { isAvailable: hasAdvancedReports } = useFeature('advancedReporting');
  const { selectedProjectId } = useProjectContext();
  const displayName = getUserDisplayName(user);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<'Weekly' | 'Monthly'>('Weekly');
  const [overdueTaskId, setOverdueTaskId] = useState<string | undefined>(undefined);
  const [myTaskId, setMyTaskId] = useState<string | undefined>(undefined);

  // Time-range filter — defaults to 'all' (current behavior)
  type TimeRange = 'all' | '7d' | '30d' | 'quarter' | 'ytd';
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    try { return (localStorage.getItem('dashboard.timeRange') as TimeRange) || 'all'; } catch { return 'all'; }
  });
  useEffect(() => { try { localStorage.setItem('dashboard.timeRange', timeRange); } catch { /* noop */ } }, [timeRange]);

  // Widget visibility — defaults to all visible (current behavior preserved)
  const DEFAULT_WIDGETS = {
    sprintHealth: true,
    priority: true,
    burndown: true,
    weeklyProgress: true,
    distribution: true,
    projects: true,
    activity: true,
    myTasks: true,
  };
  type WidgetKey = keyof typeof DEFAULT_WIDGETS;
  const [widgets, setWidgets] = useState<typeof DEFAULT_WIDGETS>(() => {
    try {
      const raw = localStorage.getItem('dashboard.widgets');
      if (!raw) return DEFAULT_WIDGETS;
      return { ...DEFAULT_WIDGETS, ...JSON.parse(raw) };
    } catch { return DEFAULT_WIDGETS; }
  });
  useEffect(() => { try { localStorage.setItem('dashboard.widgets', JSON.stringify(widgets)); } catch { /* noop */ } }, [widgets]);
  const [showCustomize, setShowCustomize] = useState(false);
  const toggleWidget = (k: WidgetKey) => setWidgets((w) => ({ ...w, [k]: !w[k] }));
  const { data: projectTasks = [], isLoading: loadingProjectTasks } = useGetAllProjectTasksQuery(
    { projectId: selectedProjectId!, sortBy: 'createdAt', sortOrder: 'asc' },
    { skip: !selectedProjectId },
  );
  const { data: allTasks = [], isLoading: loadingAllTasks } = useGetTasksQuery(
    { limit: 500 },
    { skip: !!selectedProjectId },
  );
  const tasks = selectedProjectId ? projectTasks : allTasks;
  const tasksLoading = selectedProjectId ? loadingProjectTasks : loadingAllTasks;
  const { data: projects = [], isLoading: projectsLoading, refetch: refetchProjects } = useGetProjectsQuery({});

  // Apply time-range filter (scopes by createdAt; preserves all data when 'all')
  const rangeStart = useMemo(() => {
    const today = new Date();
    switch (timeRange) {
      case '7d': return subDays(today, 7);
      case '30d': return subDays(today, 30);
      case 'quarter': return startOfQuarter(today);
      case 'ytd': return startOfYear(today);
      default: return null;
    }
  }, [timeRange]);

  const tasksAll: any[] = tasks as any[];
  const filteredTasks: any[] = useMemo(() => {
    if (!rangeStart) return tasksAll;
    return tasksAll.filter((t: any) => {
      const created = t.createdAt ? new Date(t.createdAt) : null;
      return created && created >= rangeStart;
    });
  }, [tasksAll, rangeStart]);

  // Stats — KPI metrics respect time-range filter; sprint/projects/lists use raw `tasks`
  const totalTasks = filteredTasks.length;
  const completedTasks = filteredTasks.filter((t: any) => t.status === TaskStatus.DONE).length;
  const inProgressTasks = filteredTasks.filter((t: any) => t.status === TaskStatus.IN_PROGRESS).length;
  const now = new Date();
  const overdueList = tasks.filter(
    (t: any) => t.dueDate && new Date(t.dueDate) < now && t.status !== TaskStatus.DONE
  );
  const overdueTasks = overdueList.length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Sprint health
  const { data: sprintsList = [] } = useGetSprintsQuery(selectedProjectId || undefined);
  const activeSprint = (sprintsList as any[]).find((s: any) => s.status === 'ACTIVE') || null;
  const sprintTasks = activeSprint ? tasks.filter((t: any) => t.sprintId === activeSprint.id) : [];
  const sprintDone = sprintTasks.filter((t: any) => t.status === TaskStatus.DONE).length;
  const sprintTotal = sprintTasks.length;
  const sprintProgress = sprintTotal > 0 ? Math.round((sprintDone / sprintTotal) * 100) : 0;
  const sprintDaysLeft = activeSprint?.endDate ? Math.max(0, differenceInCalendarDays(new Date(activeSprint.endDate), now)) : null;

  // Sprint Burndown — ideal vs actual remaining work over the sprint
  const burndownData = useMemo(() => {
    if (!activeSprint || !activeSprint.startDate || !activeSprint.endDate || sprintTotal === 0) return null;
    const start = new Date(activeSprint.startDate);
    const end = new Date(activeSprint.endDate);
    const totalDays = Math.max(1, differenceInCalendarDays(end, start));
    const labels: string[] = [];
    const ideal: number[] = [];
    const actual: (number | null)[] = [];
    for (let i = 0; i <= totalDays; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      labels.push(format(day, 'MMM d'));
      ideal.push(Number((sprintTotal - (sprintTotal * i) / totalDays).toFixed(1)));
      if (day <= now) {
        const completedByDay = sprintTasks.filter((t: any) =>
          t.status === TaskStatus.DONE && t.updatedAt && new Date(t.updatedAt) <= day
        ).length;
        actual.push(sprintTotal - completedByDay);
      } else {
        actual.push(null);
      }
    }
    return {
      labels,
      datasets: [
        { label: t('dashboard.chartIdeal'), data: ideal, borderColor: '#94A3B8', borderDash: [6, 4], borderWidth: 1.5, pointRadius: 0, tension: 0, fill: false },
        { label: t('dashboard.chartActual'), data: actual, borderColor: '#3B82F6', backgroundColor: 'rgba(59,130,246,.08)', borderWidth: 2, pointRadius: 3, pointBackgroundColor: '#3B82F6', tension: 0.3, fill: true, spanGaps: false },
      ],
    };
  }, [activeSprint, sprintTasks, sprintTotal, now, t]);

  // Priority breakdown (open items only)
  const urgentCount = filteredTasks.filter((t: any) => t.priority === TaskPriority.URGENT && t.status !== TaskStatus.DONE).length;
  const highCount = filteredTasks.filter((t: any) => t.priority === TaskPriority.HIGH && t.status !== TaskStatus.DONE).length;
  const blockedCount = filteredTasks.filter((t: any) => t.status === 'BLOCKED').length;

  // Upcoming deadlines (next 7 days, not done, not already overdue)
  const in7Days = new Date();
  in7Days.setDate(in7Days.getDate() + 7);
  const upcomingDeadlines = tasks
    .filter((t: any) => t.dueDate && new Date(t.dueDate) >= now && new Date(t.dueDate) <= in7Days && t.status !== TaskStatus.DONE)
    .sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    .slice(0, 5);

  // My Tasks
  const myTasks = tasks.filter((t: any) =>
    t.assignee?.id === user?.id && t.status !== TaskStatus.DONE
  ).slice(0, 5);

  // Weekly progress
  const last7Days = getLast7DayLabels();
  const completedByDay: Record<string, number> = {};
  last7Days.forEach((d) => (completedByDay[d.key] = 0));
  filteredTasks
    .filter((t: any) => t.status === TaskStatus.DONE)
    .forEach((task: any) => {
      const key = new Date(task.updatedAt).toISOString().slice(0, 10);
      if (completedByDay[key] !== undefined) completedByDay[key] += 1;
    });

  // Monthly progress (last 4 weeks)
  const last4Weeks = getLast4WeekLabels();
  const completedByWeek: Record<string, number> = {};
  last4Weeks.forEach((w) => (completedByWeek[w.label] = 0));
  filteredTasks
    .filter((t: any) => t.status === TaskStatus.DONE)
    .forEach((task: any) => {
      const key = new Date(task.updatedAt).toISOString().slice(0, 10);
      const week = last4Weeks.find((w) => key >= w.startKey && key <= w.endKey);
      if (week) completedByWeek[week.label] = (completedByWeek[week.label] || 0) + 1;
    });

  const chartData = chartPeriod === 'Weekly' ? {
    labels: last7Days.map((d) => d.label),
    data: last7Days.map((d) => completedByDay[d.key] || 0),
  } : {
    labels: last4Weeks.map((w) => w.label),
    data: last4Weeks.map((w) => completedByWeek[w.label] || 0),
  };

  const weeklyData = {
    labels: chartData.labels,
    datasets: [
      {
        label: t('dashboard.chartCompleted'),
        data: chartData.data,
        borderColor: '#1D4ED8',
        backgroundColor: 'rgba(29,78,216,.08)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#1D4ED8',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  // Donut
  const statusCounts = {
    todo: filteredTasks.filter((t: any) => t.status === TaskStatus.TODO).length,
    progress: filteredTasks.filter((t: any) => t.status === TaskStatus.IN_PROGRESS).length,
    review: filteredTasks.filter((t: any) => t.status === TaskStatus.IN_REVIEW).length,
    blocked: filteredTasks.filter((t: any) => t.status === TaskStatus.BLOCKED).length,
    done: filteredTasks.filter((t: any) => t.status === TaskStatus.DONE).length,
  };

  const donutData = {
    labels: [
      t('dashboard.statusToDo'),
      t('dashboard.statusInProgress'),
      t('dashboard.statusInReview'),
      t('dashboard.statusBlocked'),
      t('dashboard.statusDone'),
    ],
    datasets: [
      {
        data: [statusCounts.todo, statusCounts.progress, statusCounts.review, statusCounts.blocked, statusCounts.done],
        backgroundColor: ['#94A3B8', '#3B82F6', '#8B5CF6', '#EF4444', '#10B981'],
        borderWidth: 0,
      },
    ],
  };

  // Recent activity
  const recentActivity = tasks
    .slice()
    .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 6);

  return (
    <div className="relative h-full flex flex-col" style={{ backgroundColor: 'var(--bg)' }}>
      <LoadingOverlay isLoading={tasksLoading || projectsLoading} message={t('dashboard.loading')} position="fixed" />

      {/* Panel */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.10)', backgroundColor: 'var(--surface)' }}>

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="px-4 py-2 shrink-0" style={{ borderBottom: '1px solid var(--border-strong)' }}>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg shrink-0" style={{ backgroundColor: 'var(--surface-active)', border: '1px solid var(--border-focus)' }}>
            <svg className="w-3.5 h-3.5 text-indigo-600" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="6" height="6" rx="1" />
              <rect x="10" y="2" width="6" height="6" rx="1" />
              <rect x="2" y="10" width="6" height="6" rx="1" />
              <rect x="10" y="10" width="6" height="6" rx="1" />
            </svg>
          </span>
          <div className="min-w-0">
            <h1 className="text-base font-bold tracking-tight leading-none" style={{ color: 'var(--text-primary)' }}>{t('dashboard.welcome', { name: displayName })}</h1>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{t('dashboard.subtitle')}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Time-range filter */}
            <div className="hidden sm:flex items-center bg-surface-active rounded-lg p-0.5 border border-border">
              {([
                { k: 'all', label: t('dashboard.timeAll') },
                { k: '7d', label: t('dashboard.time7d') },
                { k: '30d', label: t('dashboard.time30d') },
                { k: 'quarter', label: t('dashboard.timeQuarter') },
                { k: 'ytd', label: t('dashboard.timeYtd') },
              ] as { k: TimeRange; label: string }[]).map((opt) => (
                <button
                  key={opt.k}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-md transition-colors ${timeRange === opt.k ? 'bg-white text-text-primary shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                  onClick={() => setTimeRange(opt.k)}
                  title={`Filter KPIs to ${opt.label === 'All' ? 'all time' : opt.label}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Customize widgets button */}
            <div className="relative">
              <button
                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
                onClick={() => setShowCustomize((v) => !v)}
                title="Customize dashboard widgets"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 18 18">
                  <circle cx="9" cy="9" r="2.5" />
                  <path d="M14.5 9c0-.4 0-.8-.1-1.2l1.6-1.2-1.5-2.6-1.9.6c-.6-.5-1.3-.9-2-1.1L10.2 1.5h-3l-.4 2c-.7.2-1.4.6-2 1.1l-1.9-.6L1.5 6.6 3.1 7.8c-.1.4-.1.8-.1 1.2s0 .8.1 1.2L1.5 11.4l1.5 2.6 1.9-.6c.6.5 1.3.9 2 1.1l.4 2h3l.4-2c.7-.2 1.4-.6 2-1.1l1.9.6 1.5-2.6-1.6-1.2c.1-.4.1-.8.1-1.2z" />
                </svg>
                {t('dashboard.customize')}
              </button>
              {showCustomize && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowCustomize(false)} />
                  <div className="absolute end-0 top-full mt-1 z-20 w-56 bg-white rounded-lg shadow-lg border border-border py-2">
                    <div className="px-3 py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider">{t('dashboard.widgets')}</div>
                    {([
                      { k: 'sprintHealth', label: t('dashboard.activeSprint') },
                      { k: 'priority', label: t('dashboard.openItemPriority') },
                      { k: 'burndown', label: t('dashboard.sprintBurndown') },
                      { k: 'weeklyProgress', label: t('dashboard.weeklyProgress') },
                      { k: 'distribution', label: t('dashboard.taskDistribution') },
                      { k: 'projects', label: t('dashboard.allProjectsTitle') },
                      { k: 'activity', label: t('dashboard.dueThisWeek') },
                      { k: 'myTasks', label: t('dashboard.myTasks') },
                    ] as { k: WidgetKey; label: string }[]).map((opt) => (
                      <label key={opt.k} className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-text-secondary hover:bg-surface-hover cursor-pointer">
                        <input type="checkbox" checked={widgets[opt.k]} onChange={() => toggleWidget(opt.k)} className="rounded text-brand" />
                        {opt.label}
                      </label>
                    ))}
                    <div className="border-t border-border mt-1 pt-1">
                      <button
                        className="w-full text-left px-3 py-1.5 text-[11px] text-text-muted hover:bg-surface-hover hover:text-text-primary"
                        onClick={() => { setWidgets(DEFAULT_WIDGETS); setTimeRange('all'); }}
                      >
                        {t('dashboard.resetDefaults')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-3" style={{ backgroundColor: 'var(--bg)' }}>
        <ProjectScopeBanner projectId={selectedProjectId} className="mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5 fade-up">
        <div className="stat-card fd1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-medium text-text-secondary">{t('dashboard.totalTasks')}</span>
            <div className="stat-icon" style={{ background: '#EFF6FF' }}>
              <svg className="w-4 h-4 fill-none" stroke="#2563EB" strokeWidth={2} strokeLinecap="round" viewBox="0 0 18 18">
                <path d="M2 5h14M2 9h14M2 13h8" />
              </svg>
            </div>
          </div>
          <div className="stat-value">{totalTasks}</div>
          <div className="text-[11px] text-text-muted mt-1">
            {t('dashboard.acrossProjects', { count: projects.length })}
          </div>
        </div>

        <div className="stat-card fd2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-medium text-text-secondary">{t('dashboard.completed')}</span>
            <div className="stat-icon" style={{ background: '#D1FAE5' }}>
              <svg className="w-4 h-4 fill-none" stroke="#059669" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 18 18">
                <circle cx="9" cy="9" r="7" />
                <path d="M6 9l2 2 4-4" />
              </svg>
            </div>
          </div>
          <div className="stat-value" style={{ color: '#059669' }}>{completedTasks}</div>
          <div className="text-[11px] text-text-muted mt-1">{t('dashboard.completionRate', { rate: completionRate })}</div>
        </div>

        <div className="stat-card fd3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-medium text-text-secondary">{t('dashboard.inProgress')}</span>
            <div className="stat-icon" style={{ background: '#DBEAFE' }}>
              <svg className="w-4 h-4 fill-none" stroke="#2563EB" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 18 18">
                <circle cx="9" cy="9" r="7" />
                <path d="M9 5v4l2.5 2.5" />
              </svg>
            </div>
          </div>
          <div className="stat-value" style={{ color: '#2563EB' }}>{inProgressTasks}</div>
          <div className="text-[11px] text-text-muted mt-1">{t('dashboard.activeWorkItems')}</div>
        </div>

        <div className="stat-card fd4 cursor-pointer hover:border-red-200 hover:shadow-md transition-all" onClick={() => navigate('/list')}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-medium text-text-secondary">{t('dashboard.overdue')}</span>
            <div className="stat-icon" style={{ background: '#FEE2E2' }}>
              <svg className="w-4 h-4 fill-none" stroke="#DC2626" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 18 18">
                <circle cx="9" cy="9" r="7" />
                <path d="M9 6v4M9 13h.01" />
              </svg>
            </div>
          </div>
          <div className="stat-value" style={{ color: '#DC2626' }}>{overdueTasks}</div>
          <div className="text-[11px] mt-1" style={{ color: overdueTasks > 0 ? '#DC2626' : '#6B7280' }}>
            {overdueTasks > 0 ? t('dashboard.clickToViewList') : t('dashboard.allOnTrack')}
          </div>
        </div>
      </div>

      {/* Sprint Health + Priority Breakdown + Burndown */}
      {(widgets.sprintHealth || widgets.priority || (widgets.burndown && burndownData)) && (
      <div className={`grid grid-cols-1 ${widgets.burndown && burndownData ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4 mb-5`}>
        {/* Active Sprint */}
        {widgets.sprintHealth && (
        <div className="section-card px-[22px] py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[14px] font-bold text-text-primary">{t('dashboard.activeSprint')}</h3>
              <p className="text-[12px] text-text-muted mt-0.5">
                {activeSprint ? activeSprint.name : t('dashboard.noActiveSprint')}
              </p>
            </div>
            {activeSprint && sprintDaysLeft !== null && (
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${sprintDaysLeft <= 2 ? 'bg-red-50 text-red-600' : sprintDaysLeft <= 5 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-700'}`}>
                {t('dashboard.daysLeft', { count: sprintDaysLeft })}
              </span>
            )}
          </div>
          {activeSprint ? (
            <>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12px] text-text-muted">{t('dashboard.tasksDone', { done: sprintDone, total: sprintTotal })}</span>
                <span className="text-[12px] font-semibold text-text-primary">{sprintProgress}%</span>
              </div>
              <div className="h-2 bg-border rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-brand transition-all duration-700" style={{ width: `${sprintProgress}%` }} />
              </div>
              <div className="flex gap-4 mt-3 text-[11px]">
                <span className="text-emerald-600 font-medium">{t('dashboard.doneCount', { count: sprintDone })}</span>
                <span className="text-text-muted">{t('dashboard.remainingCount', { count: sprintTotal - sprintDone })}</span>
                {blockedCount > 0 && <span className="text-red-500 font-medium">{t('dashboard.blockedCount', { count: blockedCount })}</span>}
              </div>
            </>
          ) : (
            <p className="text-[13px] text-text-muted">{t('dashboard.selectProjectForSprint')}</p>
          )}
        </div>
        )}

        {/* Open Item Priority */}
        {widgets.priority && (
        <div className="section-card px-[22px] py-4">
          <h3 className="text-[14px] font-bold text-text-primary mb-3">{t('dashboard.openItemPriority')}</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-[var(--radius-md)] bg-red-50">
              <div className="text-[26px] font-bold text-red-600">{urgentCount}</div>
              <div className="text-[10px] font-semibold text-red-500 uppercase tracking-wide mt-1">{t('dashboard.urgent')}</div>
            </div>
            <div className="text-center p-3 rounded-[var(--radius-md)] bg-orange-50">
              <div className="text-[26px] font-bold text-orange-600">{highCount}</div>
              <div className="text-[10px] font-semibold text-orange-500 uppercase tracking-wide mt-1">High</div>
            </div>
            <div className="text-center p-3 rounded-[var(--radius-md)]" style={{ background: '#FEE2E2' }}>
              <div className="text-[26px] font-bold text-red-500">{blockedCount}</div>
              <div className="text-[10px] font-semibold text-red-400 uppercase tracking-wide mt-1">{t('dashboard.blockedLabel')}</div>
            </div>
          </div>
          {(urgentCount > 0 || highCount > 0) && (
            <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-3">
              {urgentCount + highCount} high-priority item{urgentCount + highCount !== 1 ? 's' : ''} require attention
            </p>
          )}
        </div>
        )}

        {/* Sprint Burndown */}
        {widgets.burndown && burndownData && (
          <div className="section-card px-[22px] py-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-[14px] font-bold text-text-primary">{t('dashboard.sprintBurndown')}</h3>
                <p className="text-[11px] text-text-muted mt-0.5">{t('dashboard.burndownSubtitle')}</p>
              </div>
            </div>
            <div className="h-[140px]">
              <Line
                data={burndownData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom' as const, labels: { boxWidth: 10, font: { size: 10 } } } },
                  scales: {
                    y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 }, color: '#9CA3AF' }, grid: { color: '#F3F4F6' }, border: { display: false } },
                    x: { ticks: { font: { size: 9 }, color: '#9CA3AF', maxRotation: 0, autoSkip: true, maxTicksLimit: 6 }, grid: { display: false }, border: { display: false } },
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>
      )}

      {/* Charts Row — 1fr 420px */}
      {(widgets.weeklyProgress || widgets.distribution) && (
      <div className={`grid grid-cols-1 ${widgets.weeklyProgress && widgets.distribution ? 'lg:grid-cols-[1fr_420px]' : ''} gap-4 mb-5`}>
        {/* Weekly Progress */}
        {widgets.weeklyProgress && (
        <div className="chart-card">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[15px] font-bold text-text-primary">{t('dashboard.weeklyProgress')}</h3>
            <div className="flex gap-1">
              {(['Weekly', 'Monthly'] as const).map((p) => (
                <button
                  key={p}
                  className={`chart-period-btn ${chartPeriod === p ? 'active' : ''}`}
                  onClick={() => setChartPeriod(p)}
                >
                  {p === 'Weekly' ? t('dashboard.weekly') : t('dashboard.monthly')}
                </button>
              ))}
            </div>
          </div>
          <Line
            data={weeklyData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: { legend: { display: false } },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { precision: 0, font: { family: 'Figtree', size: 11 }, color: '#9CA3AF' },
                  grid: { color: '#F3F4F6' },
                  border: { display: false },
                },
                x: {
                  ticks: { font: { family: 'Figtree', size: 11 }, color: '#9CA3AF' },
                  grid: { display: false },
                  border: { display: false },
                },
              },
            }}
          />
        </div>
        )}

        {/* Task Distribution — Donut */}
        {widgets.distribution && (
        <div className="chart-card flex flex-col">
          <h3 className="text-[15px] font-bold text-text-primary mb-5">{t('dashboard.taskDistribution')}</h3>
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative" style={{ width: 180, height: 180 }}>
              <Doughnut
                data={donutData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  cutout: '70%',
                  plugins: { legend: { display: false }, tooltip: { enabled: true } },
                }}
              />
              <div className="donut-center">
                <div className="text-[26px] font-extrabold text-text-primary leading-none">{totalTasks}</div>
                <div className="text-[11px] text-text-muted">{t('dashboard.total')}</div>
              </div>
            </div>
          </div>
          {/* Legend grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4">
            {[
              { label: 'Done', count: statusCounts.done, color: '#10B981' },
              { label: 'In Progress', count: statusCounts.progress, color: '#3B82F6' },
              { label: 'In Review', count: statusCounts.review, color: '#8B5CF6' },
              { label: 'Blocked', count: statusCounts.blocked, color: '#EF4444' },
              { label: 'To Do', count: statusCounts.todo, color: '#94A3B8' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: item.color }} />
                <span className="text-[12px] text-text-secondary">{item.label}</span>
                <span className="text-[12px] font-semibold text-text-primary ml-auto">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
      )}

      {isProjectManager() && hasAdvancedReports && (
        <div className="section-card px-[22px] py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-[15px] font-bold text-text-primary">{t('dashboard.deliveryAnalytics')}</h3>
            <p className="text-[13px] text-text-muted mt-0.5">
              {t('dashboard.deliveryAnalyticsDesc')}
            </p>
          </div>
          <Link to="/reports" className="btn-action btn-action-primary shrink-0">
            {t('dashboard.openReports')}
          </Link>
        </div>
      )}

      {/* Bottom Grid — 1fr 340px */}
      {(widgets.projects || widgets.activity || widgets.myTasks) && (
      <div className={`grid grid-cols-1 ${widgets.projects && (widgets.activity || widgets.myTasks) ? 'lg:grid-cols-[1fr_340px]' : ''} gap-4`}>
        {/* All Projects */}
        {widgets.projects && (
        <div className="section-card">
          <div className="flex items-center justify-between px-[22px] py-4 border-b border-border">
            <h3 className="text-[15px] font-bold text-text-primary">{t('dashboard.allProjectsTitle')}</h3>
          </div>
          {projects.length === 0 ? (
            <div className="px-[22px] py-10 text-center text-[13px] text-text-muted">
              No projects yet. Create one to get started!
            </div>
          ) : (
            projects.slice(0, 6).map((project: any, i: number) => {
              const projectTasks = tasks.filter(
                (t: any) => t.projectId === project.id || t.project?.id === project.id
              );
              const doneTasks = projectTasks.filter((t: any) => t.status === TaskStatus.DONE).length;
              const progress = projectTasks.length > 0 ? Math.round((doneTasks / projectTasks.length) * 100) : 0;
              const color = PROJECT_COLORS[i % PROJECT_COLORS.length];
              const projOverdue = projectTasks.filter((t: any) => t.dueDate && new Date(t.dueDate) < now && t.status !== TaskStatus.DONE).length;
              const projBlocked = projectTasks.filter((t: any) => t.status === 'BLOCKED').length;
              const healthLabel = projOverdue > 0 ? t('dashboard.overdue') : projBlocked > 0 ? t('dashboard.atRisk') : t('dashboard.onTrack');
              const healthClass = projOverdue > 0 ? 'bg-red-100 text-red-600' : projBlocked > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
              return (
                <div key={project.id} className="proj-row cursor-pointer hover:bg-surface-hover transition-colors" onClick={() => navigate(`/board/${project.id}`)}>
                  <div className="w-1 h-8 rounded-full shrink-0" style={{ background: color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-[13.5px] font-semibold text-text-primary truncate">{project.name}</div>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${healthClass}`}>{healthLabel}</span>
                    </div>
                    <div className="text-[11px] text-text-muted mt-0.5">
                      {t('dashboard.projectTaskSummary', { count: projectTasks.length, done: doneTasks })}
                    </div>
                  </div>
                  <div className="w-24 shrink-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-mono font-medium text-text-secondary">{progress}%</span>
                    </div>
                    <div className="h-1.5 bg-border rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${progress}%`, background: color }}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}

        {/* Right column: overdue + my tasks */}
        {(widgets.activity || widgets.myTasks) && (
        <div className="flex flex-col gap-4">
          {/* Overdue Tasks */}
          {widgets.activity && overdueList.length > 0 && (
            <div className="section-card">
              <div className="px-[22px] py-4 border-b border-border flex items-center justify-between">
                <h3 className="text-[15px] font-bold text-red-600 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  {t('dashboard.overdueTasks')}
                </h3>
                <button
                  className="text-[11px] text-indigo-500 hover:text-indigo-700 font-semibold"
                  onClick={() => navigate('/list')}
                >
                  {t('dashboard.viewAll')}
                </button>
              </div>
              <div>
                {overdueList.slice(0, 5).map((task: any) => {
                  const daysOverdue = differenceInCalendarDays(now, new Date(task.dueDate));
                  return (
                    <div
                      key={task.id}
                      className="flex items-start gap-3 px-[22px] py-3 border-b border-border last:border-0 hover:bg-red-50/40 cursor-pointer transition-colors"
                      onClick={() => setOverdueTaskId(task.id)}
                    >
                      <div className="h-7 w-7 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                        <svg className="w-3.5 h-3.5 text-red-600" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <circle cx="9" cy="9" r="7" /><path d="M9 6v4M9 13h.01" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900 truncate">{task.title}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {task.project?.name || t('dashboard.noProject')} · {task.assignee ? getUserDisplayName(task.assignee) : t('dashboard.unassigned')}
                        </div>
                      </div>
                      <span className="text-[11px] font-bold text-red-600 shrink-0 whitespace-nowrap mt-0.5">
                        {t('dashboard.daysLate', { count: daysOverdue })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* My Tasks */}
          {widgets.myTasks && (
          <div className="section-card">
            <div className="px-[22px] py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-[15px] font-bold text-text-primary">{t('dashboard.myTasks')}</h3>
              <button
                className="text-[11px] text-indigo-500 hover:text-indigo-700 font-semibold"
                onClick={() => navigate('/list')}
              >
                View all →
              </button>
            </div>
            {myTasks.length === 0 ? (
              <div className="px-[22px] py-6 text-center text-[13px] text-text-muted">
                No tasks assigned to you.
              </div>
            ) : (
              myTasks.map((task: any) => {
                const statusColors: Record<string, string> = {
                  TODO: 'bg-gray-100 text-gray-700',
                  IN_PROGRESS: 'bg-blue-100 text-blue-700',
                  IN_REVIEW: 'bg-purple-100 text-purple-700',
                  BLOCKED: 'bg-red-100 text-red-700',
                };
                return (
                  <div
                    key={task.id}
                    className="flex items-center gap-3 px-[22px] py-3 border-b border-border last:border-0 hover:bg-surface-hover cursor-pointer transition-colors"
                    onClick={() => setMyTaskId(task.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{task.title}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{task.project?.name || t('dashboard.noProject')}</div>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full shrink-0 ${statusColors[task.status] || 'bg-gray-100 text-gray-700'}`}>
                      {task.status.replace('_', ' ')}
                    </span>
                  </div>
                );
              })
            )}
          </div>
          )}

          {/* Upcoming Deadlines / Recent Activity */}
          {widgets.activity && overdueList.length === 0 && (
            upcomingDeadlines.length > 0 ? (
              <div className="section-card">
                <div className="px-[22px] py-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-[15px] font-bold text-amber-700 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    {t('dashboard.dueThisWeek')}
                  </h3>
                  <span className="text-[11px] text-text-muted">{upcomingDeadlines.length} item{upcomingDeadlines.length !== 1 ? 's' : ''}</span>
                </div>
                <div>
                  {upcomingDeadlines.map((task: any) => {
                    const daysLeft = differenceInCalendarDays(new Date(task.dueDate), now);
                    const urgency = daysLeft === 0 ? 'bg-red-50' : daysLeft <= 2 ? 'bg-amber-50/60' : '';
                    return (
                      <div
                        key={task.id}
                        className={`flex items-start gap-3 px-[22px] py-3 border-b border-border last:border-0 hover:bg-surface-hover cursor-pointer transition-colors ${urgency}`}
                        onClick={() => setMyTaskId(task.id)}
                      >
                        <div className="h-7 w-7 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                          <svg className="w-3.5 h-3.5 text-amber-600" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <circle cx="9" cy="9" r="7" /><path d="M9 5v4l2 2" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-slate-900 truncate">{task.title}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {task.project?.name || t('dashboard.noProject')} · {task.assignee ? getUserDisplayName(task.assignee) : t('dashboard.unassigned')}
                          </div>
                        </div>
                        <span className={`text-[11px] font-bold shrink-0 whitespace-nowrap mt-0.5 ${daysLeft === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {daysLeft === 0 ? 'Today' : `${daysLeft}d`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="section-card">
                <div className="px-[22px] py-4 border-b border-border">
                  <h3 className="text-[15px] font-bold text-text-primary">{t('dashboard.recentActivity')}</h3>
                </div>
                {recentActivity.length === 0 ? (
                  <div className="px-[22px] py-10 text-center text-[13px] text-text-muted">
                    {t('dashboard.noRecentActivity')}
                  </div>
                ) : (
                  recentActivity.map((task: any, i: number) => {
                    const color = PROJECT_COLORS[i % PROJECT_COLORS.length];
                    const typeIcons: Record<string, string> = { EPIC: '⚡', FEATURE: '🔷', USER_STORY: '📘', TASK: '✅', BUG: '🐛' };
                    const timeAgo = getTimeAgo(task.updatedAt, t);
                    return (
                      <div key={task.id} className="activity-item">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[14px] shrink-0 mt-0.5">
                          {typeIcons[task.type] || '✅'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-text-primary">
                            <span className="font-semibold">{task.title}</span>
                          </div>
                          <div className="text-[11px] text-text-muted mt-0.5">
                            {(task.type || 'TASK').replace('_', ' ')} &middot; {task.status.replace('_', ' ')} &middot; {task.project?.name || t('dashboard.noProject')}
                          </div>
                        </div>
                        <span className="text-[11px] text-text-muted whitespace-nowrap shrink-0">{timeAgo}</span>
                      </div>
                    );
                  })
                )}
              </div>
            )
          )}
        </div>
        )}
      </div>
      )}
      </div>

      <ProjectCreationModal
        isOpen={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        onCreated={() => refetchProjects()}
      />
      <TaskDetailsModal
        key={overdueTaskId || 'ov-closed'}
        taskId={overdueTaskId}
        isOpen={!!overdueTaskId}
        onClose={() => setOverdueTaskId(undefined)}
      />
      <TaskDetailsModal
        key={myTaskId || 'my-closed'}
        taskId={myTaskId}
        isOpen={!!myTaskId}
        onClose={() => setMyTaskId(undefined)}
      />
      </div>
    </div>
  );
}
